import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A small hand-written markdown renderer, shared by the API docs and the user guide.
 *
 * Deliberately not a library: these are the only two markdown documents in the app and they
 * use headings, tables, fenced code, lists, inline code, bold and links — nothing more.
 *
 * Three limits worth knowing before writing markdown for it, each found by looking at a
 * rendered page rather than the source:
 *   - Code fences must start at column 0. An indented fence inside a bullet is not recognised.
 *   - Single-asterisk *italics* are not supported; use **bold**.
 *   - Links open in a new tab, so an in-page [text](#anchor) opens a blank tab. Refer to
 *     sections by name instead.
 */

const renderInline = (text: string): React.ReactNode => {
  // Order matters: code spans are extracted first so their contents are not
  // reinterpreted as bold or links.
  const tokens: React.ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }

    const [raw] = match;
    if (raw.startsWith('`')) {
      tokens.push(
        <Box
          key={key++}
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.875em',
            bgcolor: 'action.hover',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            // These documents carry 80-character URLs and ids inside backticks. An inline
            // code span will not break mid-token on its own, so on a phone one of those
            // pushes the whole page sideways. Break only where it has to.
            overflowWrap: 'anywhere',
          }}
        >
          {raw.slice(1, -1)}
        </Box>
      );
    } else if (raw.startsWith('**')) {
      // Recurse so code spans inside bold render as code rather than literal backticks:
      // the pattern matches **...** as one token, so `like this` inside it never reached
      // the code-span branch.
      tokens.push(<strong key={key++}>{renderInline(raw.slice(2, -2))}</strong>);
    } else {
      const linkMatch = /\[([^\]]+)\]\(([^)]+)\)/.exec(raw);
      if (linkMatch) {
        tokens.push(
          <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
            {linkMatch[1]}
          </a>
        );
      }
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }

  return tokens.length ? tokens : text;
};

/** One line of inline markdown — code spans, bold, links — for titles rendered outside a document. */
export const InlineMarkdown: React.FC<{ text: string }> = ({ text }) => <>{renderInline(text)}</>;

export interface HeadingAnchor {
  /** DOM id, so the page can scroll to this heading. */
  id: string;
  /** What the heading's link points at — a hash the page understands. */
  hash: string;
}

interface MarkdownProps {
  source: string;
  /**
   * Opt-in heading anchors. Return one for a heading that should get an id and a hover link;
   * undefined leaves it plain. The API docs do not pass this and render exactly as before.
   */
  anchorFor?: (level: number, text: string) => HeadingAnchor | undefined;
  /** Called when a heading's link is clicked, instead of following it. */
  onAnchorClick?: (anchor: HeadingAnchor) => void;
}

export const Markdown: React.FC<MarkdownProps> = ({ source, anchorFor, onAnchorClick }) => {
  const blocks: React.ReactNode[] = [];
  const lines = source.split('\n');
  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index];

    // Fenced code block
    if (line.startsWith('```')) {
      const code: string[] = [];
      index++;
      while (index < lines.length && !lines[index].startsWith('```')) {
        code.push(lines[index]);
        index++;
      }
      index++; // closing fence
      blocks.push(
        <Box
          key={key++}
          component="pre"
          sx={{
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            fontSize: '0.8125rem',
            fontFamily: 'monospace',
            my: 2,
          }}
        >
          {code.join('\n')}
        </Box>
      );
      continue;
    }

    // Table: a header row followed by a separator row
    if (line.startsWith('|') && index + 1 < lines.length && /^\|[\s:|-]+\|$/.test(lines[index + 1])) {
      const cells = (row: string) => row.split('|').slice(1, -1).map(c => c.trim());
      const header = cells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].startsWith('|')) {
        rows.push(cells(lines[index]));
        index++;
      }
      blocks.push(
        <Box key={key++} sx={{ overflowX: 'auto', my: 2 }}>
          {/* Squeezing four columns into a phone gives ~80px each, which wraps every cell to
              one word per line. Better to keep them legible and let the table scroll inside
              its own container, which the wrapper above provides. */}
          <Box
            component="table"
            sx={{
              borderCollapse: 'collapse',
              width: '100%',
              minWidth: { xs: 520, sm: 0 },
              fontSize: '0.875rem',
            }}
          >
            <Box component="thead">
              <Box component="tr">
                {header.map((cell, i) => (
                  <Box
                    key={i}
                    component="th"
                    sx={{ textAlign: 'left', p: 1, borderBottom: 2, borderColor: 'divider', whiteSpace: 'nowrap' }}
                  >
                    {renderInline(cell)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((row, i) => (
                <Box key={i} component="tr">
                  {row.map((cell, j) => (
                    <Box key={j} component="td" sx={{ p: 1, borderBottom: 1, borderColor: 'divider', verticalAlign: 'top' }}>
                      {renderInline(cell)}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      );
      continue;
    }

    // Headings
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const variant = (['h4', 'h5', 'h6', 'subtitle1'] as const)[level - 1];
      const anchor = anchorFor?.(level, heading[2].trim());
      blocks.push(
        <Typography
          key={key++}
          variant={variant}
          component={`h${level}` as 'h1'}
          id={anchor?.id}
          sx={{
            // Clear of the app bar when scrolled to
            scrollMarginTop: 80,
            '&:hover .anchor': { opacity: 1 },
            mt: level <= 2 ? 4 : 3,
            mb: 1.5,
            fontWeight: level <= 2 ? 600 : 500,
            // Headings here are endpoints — `POST /api/v1/posts/:id/publish` — which are one
            // unbroken code span and will not wrap on their own.
            overflowWrap: 'anywhere',
          }}
        >
          {renderInline(heading[2])}
          {anchor && (
            // A plain in-page link, unlike the document's own links which open a new tab
            <Box
              component="a"
              className="anchor"
              href={anchor.hash}
              aria-label="Link to this section"
              title="Link to this section"
              onClick={(e: React.MouseEvent) => {
                if (!onAnchorClick) return;
                e.preventDefault();
                onAnchorClick(anchor);
              }}
              sx={{
                ml: 1,
                color: 'text.disabled',
                textDecoration: 'none',
                opacity: { xs: 0.6, md: 0 },
                transition: 'opacity 0.15s',
                '&:hover': { color: 'primary.main' },
              }}
            >
              #
            </Box>
          )}
        </Typography>
      );
      index++;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      blocks.push(<Box key={key++} sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />);
      index++;
      continue;
    }

    // List (bulleted or numbered)
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const items: string[] = [];
      const ordered = /^\s*\d+\.\s+/.test(line);
      while (index < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*([-*]|\d+\.)\s+/, ''));
        index++;

        // A wrapped bullet continues on indented lines. Without this the continuation ended
        // the list and became its own paragraph, so any bullet longer than one source line
        // rendered as a bullet followed by an orphaned sentence.
        while (
          index < lines.length &&
          lines[index].trim() &&
          /^\s+/.test(lines[index]) &&
          !/^\s*([-*]|\d+\.)\s+/.test(lines[index]) &&
          !lines[index].trim().startsWith('```')
        ) {
          items[items.length - 1] += ' ' + lines[index].trim();
          index++;
        }
      }
      blocks.push(
        <Box key={key++} component={ordered ? 'ol' : 'ul'} sx={{ pl: 3, my: 1.5 }}>
          {items.map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 0.5, overflowWrap: 'break-word' }}>
              <Typography variant="body2" component="span">{renderInline(item)}</Typography>
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    // Blank line
    if (!line.trim()) {
      index++;
      continue;
    }

    // Paragraph: gather until a blank line or the start of another block
    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].startsWith('#') &&
      !lines[index].startsWith('```') &&
      !lines[index].startsWith('|') &&
      !/^\s*([-*]|\d+\.)\s+/.test(lines[index]) &&
      !/^---+$/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index]);
      index++;
    }
    blocks.push(
      <Typography key={key++} variant="body2" sx={{ my: 1.5, lineHeight: 1.7, overflowWrap: 'break-word' }}>
        {renderInline(paragraph.join(' '))}
      </Typography>
    );
  }

  return <>{blocks}</>;
};
